import { TextInput } from '@doxyme/ui';
import { useFormik } from 'formik';
import React, { createRef, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../../../global/env.config';
import { browser } from '../../../../common/services/browser';
import { PasswordField } from '../../../../components/password';
import { ReCAPTCHA } from '../../../../components/recaptcha';
import { routerHelperFactory } from '../../../../global/common/services/route-helper-factory';
import { trackCustomEventGA4 } from '../../../../global/global.tracking';
import { selectInstitution, } from '../../../../store/features/system';
import { components as Core, getAngularService } from '../../../core';
import { login, loginWithProvider, removeSignInError } from '../../actions';
import { AuthProvider, LoginErrors } from '../../constants';
import { validateSignInForm as validate } from '../../helpers';
import { getLoginError, isLoggingIn } from '../../selectors';
import './sign-in.scss';
export const SignIn = () => {
    const dispatch = useDispatch();
    const captchaRef = createRef();
    const isLoading = useSelector(isLoggingIn);
    const error = useSelector(getLoginError);
    const [shouldShowHint, setShouldShowHint] = useState(false);
    const [ready, setReady] = useState(false);
    const currentInstitution = useSelector(selectInstitution) || {};
    const { formatMessage } = useIntl();
    const institutionName = routerHelperFactory.getSubdomain();
    useEffect(() => {
        if (!institutionName || (institutionName && currentInstitution.id)) {
            setReady(true);
        }
    }, [institutionName, currentInstitution]);
    // clean up the error, it will be still passed to child in current render when it appears
    useEffect(() => {
        if (error) {
            errorHandler();
        }
        dispatch(removeSignInError());
    }, [error]);
    const emailPlaceholder = formatMessage({
        id: 'auth.sign-in.emailPlaceholder',
        defaultMessage: 'Email',
    });
    const passwordPlaceholder = formatMessage({
        id: 'auth.sign-in.passwordPlaceholder',
        defaultMessage: 'Password',
    });
    const customAuthButtonLabel = currentInstitution.customAuthButtonLabel ||
        formatMessage({
            id: 'auth.sign-in.loginWithCustom',
            defaultMessage: 'Authenticate',
        });
    const marketingUrl = routerHelperFactory.getBaseUrl();
    const trackingFactory = getAngularService('trackingFactory');
    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            rememberMe: false,
            recaptcha: null,
            shouldShowCaptcha: false,
        },
        validate,
        onSubmit: (values) => dispatch(login(values)),
    });
    const errorHandler = useCallback(() => {
        if (error.code === LoginErrors.INVALID_EMAIL_OR_PASSWORD_SHOW_HINT) {
            setShouldShowHint(true);
        }
        if (error.code ===
            LoginErrors.INVALID_EMAIL_OR_PASSWORD_SHOW_HINT_SHOW_CAPTCHA) {
            formik.setFieldValue('recaptcha', null);
            formik.setFieldValue('shouldShowCaptcha', true);
            setShouldShowHint(false);
            if (captchaRef.current) {
                captchaRef.current.reset();
            }
        }
        if (error.code !== LoginErrors.INVALID_EMAIL_OR_PASSWORD_SHOW_HINT &&
            error.code !==
                LoginErrors.INVALID_EMAIL_OR_PASSWORD_SHOW_HINT_SHOW_CAPTCHA) {
            setShouldShowHint(false);
            formik.setFieldValue('recaptcha', null);
            formik.setFieldValue('shouldShowCaptcha', false);
        }
    }, [
        error,
        shouldShowHint,
        captchaRef,
        formik.values.recaptcha,
        formik.values.shouldShowCaptcha,
    ]);
    const loginWithGoogle = useCallback((e) => {
        e.preventDefault();
        dispatch(loginWithProvider(AuthProvider.google));
    }, []);
    const loginWithSAML = useCallback(() => dispatch(loginWithProvider(AuthProvider.saml, {
        institutionId: currentInstitution.id,
        ...browser.getSystemInfo(),
    })), []);
    const trackSubmitButtonClicked = useCallback(() => {
        trackingFactory.global.trackWithoutParams('Signin Form Submitted');
        trackCustomEventGA4('login');
    }, [trackingFactory]);
    const additionalContainerClassName = currentInstitution.id && !currentInstitution.useSocialLogin
        ? 'single-column'
        : 'sign-in-box';
    const containerClassName = `content-box box-vertical ${additionalContainerClassName}`;
    const disabledSignInBtnClassName = formik.values.shouldShowCaptcha && !formik.values.recaptcha
        ? 'disabled'
        : '';
    if (!ready) {
        return null;
    }
    return (<div className="container" data-test-id="sign-in-container">
      <div className="row">
        <section className={containerClassName}>
          <header className="logo-header">
            <a className="logo-header-link" href={marketingUrl}>
              <img className="logo-header-value" alt="" src={currentInstitution.logoHorizontal ||
            '/assets/images/icons/logo-horizontal.png'}/>
            </a>
          </header>

          <form className="content" name="form" role="form" onSubmit={formik.handleSubmit}>
            {!currentInstitution.useCustomAuth && (<div>
                <div className="form-header-container">
                  <div className="form-header">
                    <h1 className="title">
                      <FormattedMessage id="auth.sign-in.title" defaultMessage="Sign In"/>
                    </h1>
                  </div>
                </div>

                <div className="col-sm-14 col-email-password">
                  <div className="form-group">
                    <TextInput className="form-control" placeholder={emailPlaceholder} type="email" spellCheck="false" data-test-id="sign-in-email-input" name="email" onChange={formik.handleChange} value={formik.values.email} hasError={formik.errors.email} errorText={formik.errors.email}/>
                  </div>

                  <div className="form-group">
                    <PasswordField className="form-control" placeholder={passwordPlaceholder} type="password" name="password" data-test-id="sign-in-password-input" value={formik.values.password} onChange={formik.handleChange} hasError={formik.errors.password} errorText={formik.errors.password}/>
                  </div>

                  {formik.values.shouldShowCaptcha && (<>
                      <p>
                        <FormattedMessage id="auth.sign-in.recaptcha" defaultMessage="Confirm you are not a robot"/>
                      </p>
                      <ReCAPTCHA className="react-recaptcha" ref={captchaRef} sitekey={config.googleReCaptchaV2CheckboxSiteKey} onChange={async (value) => formik.setFieldValue('recaptcha', value)}/>
                      {/*{formik.errors.recaptcha ? <p className="validation-invalid">{formik.errors.recaptcha}</p> : null}*/}
                    </>)}

                  {shouldShowHint && (<div className="text-center forgot-password no-bt-margin">
                      <FormattedMessage id="auth.sign-in.difficultly-logging" values={{
                    link: (<a href="/forgot-password">
                              <FormattedMessage id="auth.sign-in.difficultly-logging.reset"/>
                            </a>),
                }}/>
                    </div>)}

                  {!shouldShowHint && !formik.values.shouldShowCaptcha && (<div className="checkbox">
                      <input type="checkbox" id="rememberMe" data-test-id="sign-in-remeberMe-checkbox" name="rememberMe" value={formik.values.rememberMe} onChange={formik.handleChange}/>

                      <label htmlFor="rememberMe">
                        <div className="icon check-field-icon"/>

                        <div className="icon icon-ok"/>

                        <span>
                          <FormattedMessage id="auth.sign-in.rememberMe" defaultMessage="Remember me on this computer"/>
                        </span>
                      </label>
                    </div>)}

                  <Core.ProgressButton className={`btn btn-primary progress-button space-md-bottom ${disabledSignInBtnClassName}`} type="submit" validation-submit="form" data-test-id="sign-in-submit" isLoading={isLoading} hasError={Boolean(error)} onClick={trackSubmitButtonClicked}>
                    <span>
                      <FormattedMessage id="auth.sign-in.submit" defaultMessage="Sign In"/>
                    </span>
                  </Core.ProgressButton>
                  {!shouldShowHint && !formik.values.shouldShowCaptcha ? (<div className="text-center">
                      <a className="forgot-password" href="/forgot-password" data-test-id="sign-in-forgot-password">
                        <FormattedMessage id="auth.sign-in.forgotPassword" defaultMessage="Forgot Password?"/>
                      </a>
                    </div>) : (<br />)}
                </div>

                {currentInstitution.useSocialLogin && (<div className="col-sm-10 col-social-login">
                    <button className="btn-google" onClick={loginWithGoogle} data-test-id="sign-in-google-btn" aria-invalid="false">
                      <FormattedMessage id="auth.sign-in.loginWithGoogle" defaultMessage="Login with Google"/>
                    </button>
                  </div>)}
              </div>)}

            {currentInstitution.useCustomAuth && (<div className="form-group">
                <button className="btn btn-primary btn-custom-login progress-button" onClick={loginWithSAML}>
                  {customAuthButtonLabel}
                </button>
              </div>)}
          </form>
        </section>
      </div>
    </div>);
};
